import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Image from '../../../components/AppImage';
import { uploadImageAndGetUrl, deleteImage, getBucketForCategory } from '../../../utils/imageUpload';
import { detectMissingPromotionFiles, cleanupMissingPromotionReferences, detectMissingWorkFiles } from '../../../services/artistContentService';

const ContentSectionCard = ({ section, isExpanded, onToggle, onUpdate, saving, triggerEdit, artistId }) => {
  const [editingContent, setEditingContent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [dragStates, setDragStates] = useState({});
  const [uploadStates, setUploadStates] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [fileStatus, setFileStatus] = useState({ checking: false, missingFiles: [], hasIssues: false });
  
  const fileRefs = useRef({});

  // Handle external edit triggers from ValidationIndicators
  useEffect(() => {
    if (triggerEdit && section?.key === triggerEdit) {
      if (!isExpanded) {
        onToggle(); // First expand the section if it's not already expanded
      }
      // Trigger edit mode based on section type
      setTimeout(() => {
        if (section?.key === 'profile') {
          handleEdit({ ...section?.content, id: 'profile' });
        } else if (section?.key === 'about') {
          handleEdit(section?.content || {});
        } else if (section?.key === 'promotion') {
          handleEdit(section?.content || {});
        } else {
          // For array-based sections (calendar, work, shop), add new item
          handleEdit({ status: 'published' });
        }
      }, 300); // Small delay to allow expansion animation
    }
  }, [triggerEdit, section?.key, isExpanded, onToggle, section?.content]);

  // Enhanced missing file detection on section load
  useEffect(() => {
    if (isExpanded && artistId && (section?.key === 'promotion' || section?.key === 'work')) {
      checkForMissingFiles();
    }
  }, [isExpanded, artistId, section?.key]);

  const checkForMissingFiles = async () => {
    if (!artistId) return;

    setFileStatus(prev => ({ ...prev, checking: true }));

    try {
      let result;
      if (section?.key === 'promotion') {
        result = await detectMissingPromotionFiles(artistId);
      } else if (section?.key === 'work') {
        result = await detectMissingWorkFiles(artistId);
      }

      if (result?.success) {
        setFileStatus({
          checking: false,
          missingFiles: result?.missingFiles || [],
          hasIssues: result?.hasIssues || false
        });
      }
    } catch (error) {
      console.error('Error checking missing files:', error);
      setFileStatus(prev => ({ ...prev, checking: false }));
    }
  };

  const handleCleanupMissingFiles = async () => {
    if (!artistId || !fileStatus?.missingFiles?.length) return;

    if (!confirm(
      `Remove ${fileStatus?.missingFiles?.length} missing file references from database? ` +
      `This will clean up broken image links but won't restore the missing files.`
    )) {
      return;
    }

    try {
      let result = await cleanupMissingPromotionReferences(artistId, fileStatus?.missingFiles);
      
      if (result?.success) {
        // Refresh the section content
        onUpdate?.(result?.updatedData);
        // Clear file status
        setFileStatus({ checking: false, missingFiles: [], hasIssues: false });
        
        // Show success message
        setFormErrors({ 
          general: `✅ Successfully cleaned up ${fileStatus?.missingFiles?.length} missing file references` 
        });
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setFormErrors(prev => ({ ...prev, general: null }));
        }, 3000);
      } else {
        setFormErrors({ general: `Failed to cleanup: ${result?.error}` });
      }
    } catch (error) {
      setFormErrors({ general: 'Failed to cleanup missing files' });
      console.error('Cleanup error:', error);
    }
  };

  const handleEdit = (item) => {
    setEditingContent(item?.id || 'new');
    setEditForm(item || {});
    setFormErrors({});
  };

  const handleSave = async () => {
    try {
      // Validate form based on section type
      if (!validateForm()) {
        return;
      }

      // DATABASE COLUMN MAPPING FIX: Filter out display-only signed URL properties
      // Remove camelCase signed URL properties that are added for display but don't exist in database
      let cleanedFormData = { ...editForm };
      
      if (section?.key === 'promotion') {
        // Remove signed URL properties that should not be sent to database
        const { homeImageUrl, logoImageUrl, splashImageUrl, ...dbPromotionData } = cleanedFormData;
        cleanedFormData = dbPromotionData;
      }

      await onUpdate?.(cleanedFormData);
      setEditingContent(null);
      setEditForm({});
      setFormErrors({});
    } catch (error) {
      console.error('Error saving content:', error);
      setFormErrors({ general: 'Failed to save. Please try again.' });
    }
  };

  const handleCancel = () => {
    setEditingContent(null);
    setEditForm({});
    setFormErrors({});
  };

  const handleDelete = async (itemId) => {
    if (!confirm('Are you sure you want to delete this item?')) {
      return;
    }
    
    try {
      await onUpdate?.({ id: itemId, _action: 'delete' });
    } catch (error) {
      console.error('Error deleting content:', error);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    switch (section?.key) {
      case 'profile':
        if (!editForm?.full_name?.trim()) errors.full_name = 'Full name is required';
        if (!editForm?.email?.trim()) errors.email = 'Email is required';
        if (editForm?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(editForm?.email)) {
          errors.email = 'Please enter a valid email address';
        }
        break;
      case 'about':
        if (!editForm?.about_text?.trim()) errors.about_text = 'About text is required';
        break;
      case 'calendar':
        if (!editForm?.title?.trim()) errors.title = 'Event title is required';
        if (!editForm?.event_date) errors.event_date = 'Event date is required';
        break;
      case 'work':
        if (!editForm?.title?.trim()) errors.title = 'Work title is required';
        if (!editForm?.work_type) errors.work_type = 'Work type is required';
        break;
    }
    
    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  // Enhanced image upload handlers with immediate signed URL generation
  const handleImageUpload = async (type, file, pathKey = null) => {
    if (!file || !file?.type?.startsWith('image/') || !artistId) {
      return;
    }

    setUploadStates(prev => ({ ...prev, [type]: true }));

    try {
      // Delete old image if it exists
      if (pathKey && editForm?.[pathKey]) {
        const bucket = getBucketForCategory(section?.key === 'promotion' ? 'promotion' : 'profile');
        await deleteImage(bucket, editForm?.[pathKey]);
      }

      // Upload new image with immediate URL generation
      const category = section?.key === 'promotion' ? 'promotion' : 'profile';
      let result = await uploadImageAndGetUrl(file, artistId, category);
      
      if (result?.error) {
        setFormErrors(prev => ({ ...prev, [type]: `Upload failed: ${result?.error}` }));
        return;
      }

      // Update form data with new image path AND signed URL for immediate display
      const imagePathKey = pathKey || `${type}_path`;
      
      setEditForm(prev => ({
        ...prev,
        [imagePathKey]: result?.data?.path,
        // Add signed URL for immediate display in editor
        [`${type}DisplayUrl`]: result?.data?.url || null
      }));

    } catch (error) {
      setFormErrors(prev => ({ ...prev, [type]: 'Failed to upload image' }));
      console.error('Image upload error:', error);
    } finally {
      setUploadStates(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleDragOver = (e, type) => {
    e?.preventDefault();
    setDragStates(prev => ({ ...prev, [type]: true }));
  };

  const handleDragLeave = (e, type) => {
    e?.preventDefault();
    setDragStates(prev => ({ ...prev, [type]: false }));
  };

  const handleDrop = (e, type, pathKey) => {
    e?.preventDefault();
    setDragStates(prev => ({ ...prev, [type]: false }));
    
    const files = e?.dataTransfer?.files;
    if (files?.length > 0) {
      handleImageUpload(type, files?.[0], pathKey);
    }
  };

  const handleFileSelect = (type, e, pathKey) => {
    const file = e?.target?.files?.[0];
    if (file) {
      handleImageUpload(type, file, pathKey);
    }
  };

  const removeImage = async (type, pathKey) => {
    // Delete from storage if path exists
    if (pathKey && editForm?.[pathKey]) {
      const bucket = getBucketForCategory(section?.key === 'promotion' ? 'promotion' : 'profile');
      await deleteImage(bucket, editForm?.[pathKey]);
    }
    
    const imagePathKey = pathKey || `${type}_path`;
    
    setEditForm(prev => ({
      ...prev,
      [imagePathKey]: null,
      // Also clear the display URL
      [`${type}DisplayUrl`]: null
    }));
  };

  // IMAGE DISPLAY FIX: Enhanced Image Upload component with proper URL handling for editor
  const ImageUploadArea = ({ type, title, dimensions, currentImage, pathKey, className = "h-32" }) => {
    // CRITICAL FIX: Use display URL from form for immediate preview, fallback to signed URL from section content
    const displayImage = editForm?.[`${type}DisplayUrl`] || currentImage;
    
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-text-primary">{title}</h4>
          {dimensions && <span className="text-xs text-text-secondary">{dimensions}</span>}
        </div>
        
        <div
          className={`relative border-2 border-dashed rounded-lg p-4 transition-colors ${
            dragStates?.[type] 
              ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
          } ${uploadStates?.[type] ? 'pointer-events-none opacity-50' : ''}`}
          onDragOver={(e) => handleDragOver(e, type)}
          onDragLeave={(e) => handleDragLeave(e, type)}
          onDrop={(e) => handleDrop(e, type, pathKey)}
        >
          {uploadStates?.[type] ? (
            <div className="text-center">
              <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-sm text-text-primary">Uploading...</p>
            </div>
          ) : displayImage ? (
            <div className="relative">
              <Image
                src={displayImage}
                alt={title}
                className={`w-full ${className} object-cover rounded-lg`}
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2"
                onClick={() => removeImage(type, pathKey)}
              >
                <Icon name="X" size={14} />
              </Button>
            </div>
          ) : (
            <div className="text-center">
              <Icon name="Upload" size={24} className="mx-auto text-text-secondary mb-2" />
              <p className="text-sm text-text-primary mb-1">
                Drop image here or click to browse
              </p>
              {dimensions && (
                <p className="text-xs text-text-secondary">
                  Recommended: {dimensions}
                </p>
              )}
            </div>
          )}
          
          <input
            ref={el => fileRefs.current[type] = el}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(type, e, pathKey)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>

        {formErrors?.[type] && (
          <p className="text-sm text-destructive">{formErrors?.[type]}</p>
        )}
      </div>
    );
  };

  const renderProfileEditor = () => {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Full Name"
              value={editForm?.full_name || ''}
              onChange={(e) => setEditForm({ ...editForm, full_name: e?.target?.value })}
              error={formErrors?.full_name}
              required
            />
          </div>
          <div>
            <Input
              label="Email"
              type="email"
              value={editForm?.email || ''}
              onChange={(e) => setEditForm({ ...editForm, email: e?.target?.value })}
              error={formErrors?.email}
              required
            />
          </div>
          <div>
            <Select
              label="Artist Type"
              options={[
                { value: 'music', label: 'Music' },
                { value: 'visual', label: 'Visual Arts' },
                { value: 'actor', label: 'Acting' },
                { value: 'model', label: 'Modeling' },
                { value: 'dancer', label: 'Dance' },
                { value: 'writer', label: 'Writing' }
              ]}
              value={editForm?.artist_type || ''}
              onChange={(value) => setEditForm({ ...editForm, artist_type: value })}
            />
          </div>
          <div>
            <Input
              label="Location"
              value={editForm?.location || ''}
              onChange={(e) => setEditForm({ ...editForm, location: e?.target?.value })}
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Biography</label>
          <textarea
            value={editForm?.bio || ''}
            onChange={(e) => setEditForm({ ...editForm, bio: e?.target?.value })}
            rows={4}
            className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            placeholder="Artist biography..."
          />
        </div>
        
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={saving} size="sm">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button variant="outline" onClick={handleCancel} size="sm">
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  const renderPromotionEditor = () => {
    // SIGNED URL FIX: Generate display URLs from existing signed URLs if available
    const splashDisplayUrl = editForm?.splash_image_path ? 
      (editForm?.splashDisplayUrl || section?.content?.splashImageUrl) : null;
    const homeDisplayUrl = editForm?.home_image_path ? 
      (editForm?.homeDisplayUrl || section?.content?.homeImageUrl) : null;  
    const logoDisplayUrl = editForm?.logo_image_path ? 
      (editForm?.logoDisplayUrl || section?.content?.logoImageUrl) : null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <ImageUploadArea
            type="splash_image"
            title="Splash Screen"
            dimensions="1080 x 1920px"
            currentImage={splashDisplayUrl}
            pathKey="splash_image_path"
          />
          
          <ImageUploadArea
            type="home_image"
            title="Home Screen Background"
            dimensions="Any ratio"
            currentImage={homeDisplayUrl}
            pathKey="home_image_path"
          />
          
          <ImageUploadArea
            type="logo_image"
            title="App Logo"
            dimensions="Square (1:1)"
            currentImage={logoDisplayUrl}
            pathKey="logo_image_path"
            className="h-24"
          />
        </div>
        
        <div>
          <Input
            label="Featured Item"
            value={editForm?.featured_item || ''}
            onChange={(e) => setEditForm({ ...editForm, featured_item: e?.target?.value })}
            placeholder="Enter featured content description"
          />
        </div>
        
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={saving} size="sm">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button variant="outline" onClick={handleCancel} size="sm">
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  const renderAboutEditor = () => {
    // SIGNED URL FIX: Generate display URL from existing signed URL if available
    const headerDisplayUrl = editForm?.header_image_path ? 
      (editForm?.headerDisplayUrl || section?.content?.headerImageUrl) : null;

    return (
      <div className="space-y-6">
        <ImageUploadArea
          type="header_image"
          title="Header Image"
          dimensions="1200 x 400px"
          currentImage={headerDisplayUrl}
          pathKey="header_image_path"
          className="h-40"
        />
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">About Text</label>
          <textarea
            value={editForm?.about_text || ''}
            onChange={(e) => setEditForm({ ...editForm, about_text: e?.target?.value })}
            rows={8}
            className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            placeholder="Write about this artist..."
          />
          {formErrors?.about_text && (
            <p className="mt-1 text-sm text-destructive">{formErrors?.about_text}</p>
          )}
        </div>
        
        <div>
          <h4 className="text-lg font-medium text-text-primary mb-4">Booking Contact</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Contact Name"
              value={editForm?.booking_contact?.name || ''}
              onChange={(e) => setEditForm({ 
                ...editForm, 
                booking_contact: { 
                  ...editForm?.booking_contact, 
                  name: e?.target?.value 
                }
              })}
            />
            <Input
              label="Contact Email"
              type="email"
              value={editForm?.booking_contact?.email || ''}
              onChange={(e) => setEditForm({ 
                ...editForm, 
                booking_contact: { 
                  ...editForm?.booking_contact, 
                  email: e?.target?.value 
                }
              })}
            />
            <Input
              label="Contact Phone"
              type="tel"
              value={editForm?.booking_contact?.phone || ''}
              onChange={(e) => setEditForm({ 
                ...editForm, 
                booking_contact: { 
                  ...editForm?.booking_contact, 
                  phone: e?.target?.value 
                }
              })}
            />
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={saving} size="sm">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button variant="outline" onClick={handleCancel} size="sm">
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  const renderCalendarEditor = () => {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Event Title"
            value={editForm?.title || ''}
            onChange={(e) => setEditForm({ ...editForm, title: e?.target?.value })}
            error={formErrors?.title}
            required
          />
          <Input
            label="Event Date"
            type="date"
            value={editForm?.event_date || ''}
            onChange={(e) => setEditForm({ ...editForm, event_date: e?.target?.value })}
            error={formErrors?.event_date}
            required
          />
          <Input
            label="Event Time"
            type="time"
            value={editForm?.event_time || ''}
            onChange={(e) => setEditForm({ ...editForm, event_time: e?.target?.value })}
          />
          <Input
            label="Location"
            value={editForm?.location || ''}
            onChange={(e) => setEditForm({ ...editForm, location: e?.target?.value })}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Event Description</label>
          <textarea
            value={editForm?.body || ''}
            onChange={(e) => setEditForm({ ...editForm, body: e?.target?.value })}
            rows={4}
            className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            placeholder="Describe the event..."
          />
        </div>
        
        <div>
          <Select
            label="Status"
            options={[
              { value: 'published', label: 'Published' },
              { value: 'draft', label: 'Draft' },
              { value: 'cancelled', label: 'Cancelled' }
            ]}
            value={editForm?.status || 'published'}
            onChange={(value) => setEditForm({ ...editForm, status: value })}
          />
        </div>
        
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={saving} size="sm">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button variant="outline" onClick={handleCancel} size="sm">
            Cancel
          </Button>
          {editForm?.id && (
            <Button 
              variant="destructive" 
              onClick={() => handleDelete(editForm?.id)} 
              size="sm"
            >
              Delete Event
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderWorkEditor = () => {
    // SIGNED URL FIX: Generate display URL from existing signed URL if available
    const workDisplayUrl = editForm?.image_path ? 
      (editForm?.workDisplayUrl || editForm?.imageUrl) : null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Work Title"
            value={editForm?.title || ''}
            onChange={(e) => setEditForm({ ...editForm, title: e?.target?.value })}
            error={formErrors?.title}
            required
          />
          <Select
            label="Work Type"
            options={[
              { value: 'painting', label: 'Painting' },
              { value: 'sculpture', label: 'Sculpture' },
              { value: 'photography', label: 'Photography' },
              { value: 'digital', label: 'Digital Art' },
              { value: 'mixed_media', label: 'Mixed Media' },
              { value: 'other', label: 'Other' }
            ]}
            value={editForm?.work_type || ''}
            onChange={(value) => setEditForm({ ...editForm, work_type: value })}
            error={formErrors?.work_type}
            required
          />
          <Input
            label="Price"
            type="number"
            value={editForm?.price || ''}
            onChange={(e) => setEditForm({ ...editForm, price: e?.target?.value })}
            placeholder="0.00"
          />
          <Input
            label="Dimensions"
            value={editForm?.dimensions || ''}
            onChange={(e) => setEditForm({ ...editForm, dimensions: e?.target?.value })}
            placeholder="e.g., 24 x 36 inches"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">Description</label>
          <textarea
            value={editForm?.description || ''}
            onChange={(e) => setEditForm({ ...editForm, description: e?.target?.value })}
            rows={4}
            className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            placeholder="Describe this work..."
          />
        </div>
        
        <ImageUploadArea
          type="work_image"
          title="Work Image"
          dimensions="Square preferred"
          currentImage={workDisplayUrl}
          pathKey="image_path"
        />
        
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={editForm?.include_in_shop || false}
              onChange={(e) => setEditForm({ ...editForm, include_in_shop: e?.target?.checked })}
              className="rounded border-border focus:ring-2 focus:ring-primary"
            />
            <span className="text-sm text-text-primary">Include in Shop</span>
          </label>
          
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={editForm?.collectible || false}
              onChange={(e) => setEditForm({ ...editForm, collectible: e?.target?.checked })}
              className="rounded border-border focus:ring-2 focus:ring-primary"
            />
            <span className="text-sm text-text-primary">Mark as Collectible</span>
          </label>
        </div>
        
        <div>
          <Select
            label="Status"
            options={[
              { value: 'published', label: 'Published' },
              { value: 'draft', label: 'Draft' },
              { value: 'sold', label: 'Sold' }
            ]}
            value={editForm?.status || 'published'}
            onChange={(value) => setEditForm({ ...editForm, status: value })}
          />
        </div>
        
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={saving} size="sm">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button variant="outline" onClick={handleCancel} size="sm">
            Cancel
          </Button>
          {editForm?.id && (
            <Button 
              variant="destructive" 
              onClick={() => handleDelete(editForm?.id)} 
              size="sm"
            >
              Delete Work
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderProfileContent = () => {
    const profile = section?.content;
    if (!profile) return <p className="text-text-secondary">No profile data available</p>;

    if (editingContent === 'profile') {
      return renderProfileEditor();
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm font-medium text-text-secondary">Full Name</span>
            <p className="text-text-primary">{profile?.full_name || 'Not provided'}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-text-secondary">Email</span>
            <p className="text-text-primary">{profile?.email || 'Not provided'}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-text-secondary">Artist Type</span>
            <p className="text-text-primary capitalize">{profile?.artist_type || 'Not specified'}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-text-secondary">Location</span>
            <p className="text-text-primary">{profile?.location || 'Not provided'}</p>
          </div>
        </div>
        {profile?.bio && (
          <div>
            <span className="text-sm font-medium text-text-secondary">Biography</span>
            <p className="text-text-primary mt-1">{profile?.bio}</p>
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleEdit({ ...profile, id: 'profile' })}
          iconName="Edit"
          iconPosition="left"
        >
          Edit Profile
        </Button>
      </div>
    );
  };

  const renderPromotionContent = () => {
    const promotion = section?.content;
    
    if (editingContent && (editingContent === promotion?.id || editingContent === 'new')) {
      return renderPromotionEditor();
    }

    if (!promotion) {
      return (
        <div className="text-center py-8">
          <p className="text-text-secondary mb-4">No promotion materials uploaded</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleEdit({})}
            iconName="Upload"
            iconPosition="left"
          >
            Add Promotion Assets
          </Button>
        </div>
      );
    }

    // ENHANCED MISSING FILES DETECTION: Use service-level detection results
    const hasMissingFiles = fileStatus?.hasIssues || (
      (promotion?.splash_image_path && !promotion?.splashImageUrl) ||
      (promotion?.home_image_path && !promotion?.homeImageUrl) ||
      (promotion?.logo_image_path && !promotion?.logoImageUrl)
    );

    return (
      <div className="space-y-4">
        {/* FILE STATUS CHECKER: Show checking status */}
        {fileStatus?.checking && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
            <div className="flex items-center">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-3"></div>
              <span className="text-blue-800 text-sm">Checking file integrity...</span>
            </div>
          </div>
        )}

        {/* ENHANCED MISSING FILES WARNING: Show detailed missing file information */}
        {hasMissingFiles && !fileStatus?.checking && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex items-start">
              <Icon name="AlertCircle" className="text-red-600 mt-0.5 mr-3 flex-shrink-0" size={18} />
              <div className="flex-1">
                <h4 className="text-red-800 font-medium text-sm">Missing Promotion Files for Ryan Culver</h4>
                <p className="text-red-700 text-sm mt-1">
                  Database contains references to {fileStatus?.missingFiles?.length || 'several'} images that no longer exist in Supabase storage.
                </p>
                
                {/* Detailed missing files list */}
                {fileStatus?.missingFiles?.length > 0 && (
                  <div className="mt-2 text-xs text-red-600">
                    <p className="font-medium">Missing files:</p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      {fileStatus?.missingFiles?.map((file, index) => (
                        <li key={index}>
                          <span className="font-medium capitalize">{file?.field?.replace('_image_path', ' image')}</span>: 
                          <span className="ml-1 font-mono text-xs">{file?.path?.split('/')?.pop()}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm" 
                    onClick={() => handleEdit(promotion)}
                    className="text-red-800 border-red-300 hover:bg-red-100"
                  >
                    <Icon name="Upload" size={14} className="mr-1" />
                    Re-upload Missing Images
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCleanupMissingFiles}
                    className="text-red-800 border-red-300 hover:bg-red-100"
                  >
                    <Icon name="Trash2" size={14} className="mr-1" />
                    Clean Up Database References
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={checkForMissingFiles}
                    className="text-red-800 border-red-300 hover:bg-red-100"
                  >
                    <Icon name="RefreshCw" size={14} className="mr-1" />
                    Recheck Files
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SUCCESS STATUS: Show when files are all present */}
        {!hasMissingFiles && !fileStatus?.checking && isExpanded && (
          <div className="bg-green-50 border-l-4 border-green-400 p-3">
            <div className="flex items-center">
              <Icon name="CheckCircle" className="text-green-600 mr-2 flex-shrink-0" size={16} />
              <span className="text-green-800 text-sm">All promotion files verified and accessible</span>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <span className="text-sm font-medium text-text-secondary">Splash Screen</span>
            {promotion?.splash_image_path ? (
              <div className="mt-2 w-full h-32 bg-gray-100 rounded border overflow-hidden">
                {promotion?.splashImageUrl ? (
                  <Image
                    src={promotion?.splashImageUrl}
                    alt="Splash screen"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-center p-2">
                    <Icon name="AlertTriangle" className="text-red-400 mb-1" size={20} />
                    <span className="text-xs text-red-600 font-medium">File Missing</span>
                    <span className="text-xs text-gray-500 mt-1 break-all px-1">
                      {promotion?.splash_image_path?.split('/')?.pop()}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-text-secondary text-sm mt-1">No splash screen</p>
            )}
          </div>
          <div>
            <span className="text-sm font-medium text-text-secondary">Logo</span>
            {promotion?.logo_image_path ? (
              <div className="mt-2 w-full h-32 bg-gray-100 rounded border overflow-hidden">
                {promotion?.logoImageUrl ? (
                  <Image
                    src={promotion?.logoImageUrl}
                    alt="Logo"
                    className="w-full h-full object-contain p-2"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-center p-2">
                    <Icon name="AlertTriangle" className="text-red-400 mb-1" size={20} />
                    <span className="text-xs text-red-600 font-medium">File Missing</span>
                    <span className="text-xs text-gray-500 mt-1 break-all px-1">
                      {promotion?.logo_image_path?.split('/')?.pop()}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-text-secondary text-sm mt-1">No logo</p>
            )}
          </div>
          <div>
            <span className="text-sm font-medium text-text-secondary">Home Image</span>
            {promotion?.home_image_path ? (
              <div className="mt-2 w-full h-32 bg-gray-100 rounded border overflow-hidden">
                {promotion?.homeImageUrl ? (
                  <Image
                    src={promotion?.homeImageUrl}
                    alt="Home image"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-center p-2">
                    <Icon name="AlertTriangle" className="text-red-400 mb-1" size={20} />
                    <span className="text-xs text-red-600 font-medium">File Missing</span>
                    <span className="text-xs text-gray-500 mt-1 break-all px-1">
                      {promotion?.home_image_path?.split('/')?.pop()}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-text-secondary text-sm mt-1">No home image</p>
            )}
          </div>
        </div>
        {promotion?.featured_item && (
          <div>
            <span className="text-sm font-medium text-text-secondary">Featured Item</span>
            <p className="text-text-primary mt-1">{promotion?.featured_item}</p>
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleEdit(promotion)}
          iconName="Upload"
          iconPosition="left"
        >
          Manage Promotion Assets
        </Button>
      </div>
    );
  };

  const renderAboutContent = () => {
    const about = section?.content;
    
    if (editingContent && (editingContent === about?.id || editingContent === 'new')) {
      return renderAboutEditor();
    }

    if (!about) {
      return (
        <div className="text-center py-8">
          <p className="text-text-secondary mb-4">No about section content</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleEdit({})}
            iconName="Plus"
            iconPosition="left"
          >
            Add About Content
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {about?.about_text ? (
          <div>
            <span className="text-sm font-medium text-text-secondary">About Text</span>
            <div className="mt-2 p-4 bg-gray-50 rounded border">
              <p className="text-text-primary whitespace-pre-wrap">{about?.about_text}</p>
            </div>
          </div>
        ) : (
          <p className="text-text-secondary">No about text provided</p>
        )}
        
        {about?.header_image_path && (
          <div>
            <span className="text-sm font-medium text-text-secondary">Header Image</span>
            <div className="mt-2 w-full h-32 bg-gray-100 rounded border flex items-center justify-center">
              <Icon name="Image" className="text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Header Image</span>
            </div>
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => handleEdit(about)}
          iconName="Edit"
          iconPosition="left"
        >
          Edit About Section
        </Button>
      </div>
    );
  };

  const renderCalendarContent = () => {
    const events = section?.content || [];
    
    if (editingContent && editingContent !== null) {
      return renderCalendarEditor();
    }
    
    if (events?.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-text-secondary mb-4">No calendar events scheduled</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleEdit({ status: 'published' })}
            iconName="Plus"
            iconPosition="left"
          >
            Add New Event
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="space-y-3">
          {events?.map((event) => (
            <div key={event?.id} className="p-4 border border-border rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-text-primary">{event?.title}</h4>
                  <div className="flex items-center gap-4 mt-1 text-sm text-text-secondary">
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      <span>{new Date(event?.event_date)?.toLocaleDateString()}</span>
                    </div>
                    {event?.event_time && (
                      <div className="flex items-center gap-1">
                        <Icon name="Clock" size={14} />
                        <span>{event?.event_time}</span>
                      </div>
                    )}
                  </div>
                  {event?.body && (
                    <p className="text-text-secondary text-sm mt-2 line-clamp-2">{event?.body}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(event)}
                    iconName="Edit"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(event?.id)}
                    iconName="Trash2"
                    className="text-destructive hover:text-destructive"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleEdit({ status: 'published' })}
          iconName="Plus"
          iconPosition="left"
        >
          Add New Event
        </Button>
      </div>
    );
  };

  const renderWorkContent = () => {
    const works = section?.content || [];
    
    if (editingContent && editingContent !== null) {
      return renderWorkEditor();
    }
    
    if (works?.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-text-secondary mb-4">No portfolio works added</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleEdit({ status: 'published' })}
            iconName="Plus"
            iconPosition="left"
          >
            Add New Work
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {works?.slice(0, 4)?.map((work) => (
            <div key={work?.id} className="p-4 border border-border rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-text-primary">{work?.title}</h4>
                  <div className="flex items-center gap-2 mt-1 text-sm text-text-secondary">
                    {work?.work_type && (
                      <span className="capitalize">{work?.work_type}</span>
                    )}
                    {work?.price && (
                      <>
                        <span>•</span>
                        <span>${work?.price}</span>
                      </>
                    )}
                  </div>
                  {work?.dimensions && (
                    <p className="text-text-secondary text-sm mt-1">{work?.dimensions}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(work)}
                    iconName="Edit"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(work?.id)}
                    iconName="Trash2"
                    className="text-destructive hover:text-destructive"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        {works?.length > 4 && (
          <p className="text-sm text-text-secondary">
            Showing 4 of {works?.length} works. 
            <button className="text-primary hover:underline ml-1">View all</button>
          </p>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleEdit({ status: 'published' })}
          iconName="Plus"
          iconPosition="left"
        >
          Add New Work
        </Button>
      </div>
    );
  };

  const renderShopContent = () => {
    const shopItems = section?.content || [];
    
    // Shop items are filtered works, so use work editor
    if (editingContent && editingContent !== null) {
      return renderWorkEditor();
    }
    
    if (shopItems?.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-text-secondary mb-4">No shop items available</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleEdit({ include_in_shop: true, status: 'published' })}
            iconName="Plus"
            iconPosition="left"
          >
            Add Shop Item
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {shopItems?.slice(0, 4)?.map((item) => (
            <div key={item?.id} className="p-4 border border-border rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-text-primary">{item?.title}</h4>
                  <div className="flex items-center gap-2 mt-1 text-sm">
                    <span className="font-medium text-primary">${item?.price}</span>
                    {item?.collectible && (
                      <span className="px-2 py-1 text-xs bg-accent text-accent-foreground rounded">
                        Collectible
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(item)}
                    iconName="Edit"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(item?.id)}
                    iconName="Trash2"
                    className="text-destructive hover:text-destructive"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        {shopItems?.length > 4 && (
          <p className="text-sm text-text-secondary">
            Showing 4 of {shopItems?.length} items.
            <button className="text-primary hover:underline ml-1">View all</button>
          </p>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleEdit({ include_in_shop: true, status: 'published' })}
          iconName="Plus"
          iconPosition="left"
        >
          Add Shop Item
        </Button>
      </div>
    );
  };

  const renderSectionContent = () => {
    switch (section?.key) {
      case 'profile':
        return renderProfileContent();
      case 'promotion':
        return renderPromotionContent();
      case 'about':
        return renderAboutContent();
      case 'calendar':
        return renderCalendarContent();
      case 'work':
        return renderWorkContent();
      case 'shop':
        return renderShopContent();
      default:
        return <p className="text-text-secondary">Content type not supported</p>;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <Icon name={section?.icon} size={20} className="text-primary" />
          <div>
            <h3 className="font-medium text-text-primary">{section?.title}</h3>
            <p className="text-sm text-text-secondary">{section?.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${
            section?.validation?.complete ? 'bg-green-500' : 'bg-gray-300'
          }`} />
          <span className="text-sm text-text-secondary">
            {section?.validation?.complete ? 'Complete' : 'Incomplete'}
          </span>
        </div>
      </div>
      
      {isExpanded && (
        <div className="border-t border-border p-4">
          {formErrors?.general && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{formErrors?.general}</p>
            </div>
          )}
          {renderSectionContent()}
        </div>
      )}
    </div>
  );
};

export default ContentSectionCard;